/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

const View = require('lib/@waca/core-client/js/core-client/ui/core/View');



import CustomPalette from 'dialog/CustomPalette';
import StringResources from 'nls/StringResources';
import _ from 'underscore';
import React from 'react';
import ReactDOM from 'react-dom';
import $ from 'jquery';


/**
	Class that will take the palettes, generate a property spec spec and renders it
**/

const DEFAULT_MENUITEMS = {
	'my': ['edit', 'duplicate', 'delete'],
	'local': ['duplicate'],
	'public': ['duplicate'],
	'system': ['duplicate'],
	'legacy': []
};

const PALETTE_SECTION_ORDER = ['mru', 'local', 'my', 'public', 'legacy', 'system'];

const ChangePaletteView = View.extend({

	/**
		 * Creates a slideout view that renders a list of palettes
		 * @param {node} options.el - parent element
		 * @param {String} options.name - property name
		 * @param {Object} options.isAdminUI - {boolean} boolean to duplicate items in global instead
		 * @param {Object} options.glassContext - Glass context
		 * @param {Object} options.menuItems - Object that represents the menu items shown for each type of palettes (my, public, system). By default : DEFAULT_MENUITEMS
		 * @param {Function} options.getPalettes - function to call to get the list of palettes. Must return a promise that resolved with the palettes
 		 * @param {String} options.selectedId - Palette that is currently selected
		 * @param {Function} [options.onCreatePalette]- callback called after creating a palette
		 * @param {Function} options.onCloseCb - callback before closing the view
		 * @param {Function} options.onChange - callback when we change palette
		 * @param {Boolean} [options.showHeader] - Should the header be show?  Default is true
		 * @param {String} options.createPaletteType - Type of palette to create when launching the palette dialog
		 * @param {String} options.name - property name
		 * @param {Boolean} [options.useSections] - Should the palettes be rendered inside of collapsible sections?  Default is true.
		 * @param {String} [options.component] - Used for MRU logic, currently supports 'dashboard' and 'reporting'
		 * @param {Boolean} [options.showRecentlyUsed] - Should the list of recently used palettes be displayed
	*/

	init: function (options) {
		ChangePaletteView.inherited('init', this, arguments);
		this._sectionOpenState = {}; // Use to track which section are open/closed for when we need to refresh the view
		this._initialSelectedId = options.selectedId;

		_.extend(this, options);
	},

	_createBannerSpec: function () {
		return {
			'type': 'Banner',
			'value': StringResources.get('changePaletteLabel'),
			'name': 'changePaletteBanner',
			'id': 'changePaletteBanner',
			'centerLabel': true,
			'onClose': () => {
				this._handleUpdatingMRU();

				if (this.onCloseCb) {
					this.onCloseCb();
				} else if (this.slideout) {
					this.slideout.hide();
				}
			},
			'clickables': [{
				'type': 'icon',
				'name': 'addNewPalette',
				'svgIcon': 'common-add-new',
				'iconTooltip': StringResources.get('createPalette'),
				'clickCallback': this._openPaletteCreator.bind(this)
			}],
			'backButton': true
		};
	},


	_openPaletteCreator: function () {
		let paletteComponent = <CustomPalette
			key='addNewPalette'
			glassContext={this.glassContext}
			onPaletteCreated={this.onPaletteCreated.bind(this)}
			paletteType={this.createPaletteType}
			removeDialog={() => {
				$(document.body).find('.paletteDialogContainer').remove();
			}}
			ref={(instance) => {
				this.palleteDialog = instance;
			}}>
		</CustomPalette>;
		let paletteContainer = $(document.body).find('.paletteDialogContainer');
		if (paletteContainer.length === 0) {
			paletteContainer = $('<div class="paletteDialogContainer">');
			$(document.body).append(paletteContainer);
		}
		ReactDOM.render(paletteComponent, paletteContainer.get(0));
		this.palleteDialog.openDialog();
	},

	_createSectionSpec: function (paletteCategory) {
		let label = paletteCategory;
		if (paletteCategory === 'my') {
			label = 'custom';
		} else if (paletteCategory === 'public') {
			label= 'global';
		}
		const ret = {
			type: 'CollapsibleSection',
			label: StringResources.get(`${label}PaletteLabel`),
			id: 'sectionLabel' + paletteCategory,
			name: paletteCategory,
			styleAsSimpleRow: true,
			hideSectionTitle: this.hideSectionTitle ? this.hideSectionTitle : false,
			items: [],
			onOpenChange: (name, isOpen) => {
				// Need to keep track which section is opened/closed for when we refresh the view
				this._sectionOpenState[name] = isOpen;
			}
		};
		if (paletteCategory === 'legacy') {
			ret['sectionExtraMessage'] = StringResources.get('legacySectionExtraMessage');
		}
		return ret;
	},

	_createPaletteSpec: function (palettes, properties) {
		PALETTE_SECTION_ORDER.forEach((sectionName) => {
			if (palettes[sectionName] && palettes[sectionName].length > 0) {
				let collapsibleSpec = null;
				if (this.useSections !== false) {
					collapsibleSpec = this._createSectionSpec(sectionName);
					properties.push(collapsibleSpec);
				}
				const containerProperty = collapsibleSpec ? collapsibleSpec : properties;
				palettes[sectionName].forEach((palette) => {
					const isSelected = this.selectedId === palette.id;
					let propertySpec = {
						type: 'NewPalette',
						id: palette.id,
						name: this.name,
						reverse: this.reverse,
						selected: isSelected,
						palette: palette,
						createPaletteType: this.createPaletteType,
						isAdminUI: this.isAdminUI,
						onChange: this.onPaletteChange.bind(this),
						readOnly: false
					};

					if (this.menuItems && this.menuItems[palette.section]) {
						propertySpec.menuItems = this.menuItems[palette.section];
					} else {
						propertySpec.menuItems = DEFAULT_MENUITEMS[palette.section];
					}

					if (this.useSections !== false) {
						if (isSelected && typeof this._sectionOpenState[sectionName] === 'undefined') {
							this._sectionOpenState[sectionName] = true;
						}

						if (this._sectionOpenState[sectionName]) {
							containerProperty.open = true;
						}
						containerProperty.items.push(propertySpec);
					} else {
						containerProperty.push(propertySpec);
					}
				});
			}
		});
	},

	_getPropertySpec() {
		let properties = [];

		if (this.showHeader !== false) {
			properties.push(this._createBannerSpec());
		}

		return this.getPalettes().then((palettes) => {
			this._augmentPalettes(palettes);
			this.palettes = palettes;
			this._trimCustomPalettes(palettes);
			return this._getMRUPalettes().then((mruPalettes) => {
				palettes.mru = mruPalettes || [];

				this._createPaletteSpec(palettes, properties);

				if (properties.length === 0) {
					return null;
				}

				return {
					el: this.$el,
					glassContext: this.glassContext,
					items: properties,
					ariaLabel: properties[0].value,
					primaryUIControl: true
				};
			});
		});
	},

	/**
	 * We need to augement the palette information with which section the palette is being shown in
	 * @param {Array} palettes
	 */
	_augmentPalettes(palettes) {
		if (!palettes) {
			return;
		}

		for (let section in palettes) {
			if (palettes[section] && palettes[section].length > 0) {
				palettes[section].forEach((palette) => {
					palette.section = section;
				});
			}
		}
	},

	_getMRUPalettes() {
		const mruPalettes = [];
		if (this.showRecentlyUsed === false || !this._paletteService) {
			return Promise.resolve(mruPalettes);
		}

		return this._paletteService.getRecentlyUsedPaletteInfo(this._getFillType()).then((mruInfo) => {
			mruInfo.forEach((info) => {
				// Cap the MRU list of palettes to 3
				if (mruPalettes.length < 3) {
					// Only add palettes to the MRU if there already somewhere else in the view (custom, global, system)
					const palette = this._findPaletteFromId(info.id);
					if (palette) {
						mruPalettes.push(palette);
					}
				}
			});

			return mruPalettes;
		});
	},

	/**
	 * Gets the fill type of the palettes being displayed.  Assumes there's only one fillType at a time, so either all
	 * simple or all continuous
	 */
	_getFillType() {
		if (this._fillType) {
			return this._fillType;
		}

		for (let key in this.palettes) {
			if (this.palettes[key] && this.palettes[key].length > 0) {
				const palettes = this.palettes[key];
				for (let i = 0; i < palettes.length; i++) {
					if (palettes[i].content && palettes[i].content.fillType) {
						this._fillType = palettes[i].content.fillType;
						return this._fillType;
					}
				}
			}
		}
	},

	/**
	 * Given an ID, search all the sections (custom, local, global, system) for a matching ID
	 */
	_findPaletteFromId(id) {
		if (!this.palettes) {
			return null;
		}

		// Make sure we compare the base CM ID without any prefixes
		let safeId = this._getRealPaletteId(id);
		for (let key in this.palettes) {
			if (this.palettes[key]) {
				const match = _.find(this.palettes[key], (palette) => {
					return this._getRealPaletteId(palette.id) === safeId;
				});
				if (match) {
					return match;
				}
			}
		}

		return null;
	},

	/**
	 * Make sure the same palette doesn't show up in the custom AND global sections
	 * @param {Array} palettes
	 */
	_trimCustomPalettes(palettes = {}) {
		const myPalettes = palettes.my;
		const globalPalettes = palettes.public;

		const findMatch = (id) => {
			return _.find(globalPalettes, (palette) => {
				return palette.id === id;
			});
		};

		if (myPalettes && globalPalettes) {
			for (let i = 0; i < myPalettes.length; i++) {
				const id = myPalettes[i].id;
				if (findMatch(id)) {
					myPalettes.splice(i, 1);
					i--;
				}
			}
		}
	},

	onPaletteCreated: function(paletteId){
		if(this.onCreatePalette){
			this.selectedId = this.onCreatePalette(paletteId);
		}
	},

	_registerPaletteServiceEvents() {
		if (this._paletteService && !this._regisered) {
			this._regisered = true;
			this._paletteService.on('palette:created', this.render, this);
			this._paletteService.on('palette:updated', this._onPaletteUpdate, this);
			this._paletteService.on('palette:deleted', this._onPaletteDelete, this);
		}
	},

	_unregisterPaletteServiceEvents() {
		if (this._paletteService) {
			this._paletteService.off('palette:created', this.render, this);
			this._paletteService.off('palette:updated', this._onPaletteUpdate, this);
			this._paletteService.off('palette:deleted', this._onPaletteDelete, this);
		}
	},


	/**
	 * Update the internal selectedId and call the onChange callback
	 */
	onPaletteChange(propertyName, paletteItem) {
		this.selectedId = paletteItem.id;
		if (this.onChange) {
			this.onChange(propertyName, paletteItem);
		}
	},

	_handleUpdatingMRU() {
		// If the user changed the palette, then update the recently used list
		if (this.selectedId !== this._initialSelectedId) {
			const palette = this._findPaletteFromId(this.selectedId);

			// Don't add local palettes to the MRU list
			if (palette && palette.section !== 'local') {
				const propName = this.name || '';

				// Dashboard has a different list of system palettes for heat and conditional.  Make sure
				// we keep 3 palettes in the MRU for both sets
				const trimSystemByPropName = propName === 'condColorPalette' || propName.indexOf('contColorPalette') === 0 ? true : false;

				this._paletteService.addRecentlyUsedPalette({
					palette: palette,
					isSystem: palette.section === 'system',
					component: this.component,
					propName: propName,
					trimSystemByPropName: trimSystemByPropName
				});
			}
		}
	},

	/**
	 * Palette IDs sometime have a prefix to let us know it's a CM palette.  This will
	 * remove any prefix
	 */
	_getRealPaletteId(id) {
		return this._paletteService.getCMSafePaletteId(id);
	},

	_onPaletteUpdate({ paletteId }) {
		this._updatePaletteProperties({
			properties: this._propertyUIControl.getProperties(),
			paletteIdToUpdate: paletteId,
			action: 'update'
		});
	},

	/**
	 * Method to update properties inline without having to refresh the entire pane.  Only works
	 * for updates and if deleting a palette that you currently don't have selected.
	 */
	_updatePaletteProperties({ properties, paletteIdToUpdate, action, parentProperty }) {
		for (let i = 0; i < properties.length; i++) {
			const property = properties[i];
			if (property.type === 'CollapsibleSection') {
				this._updatePaletteProperties({
					properties: property._oPropertyUIControl.getProperties(),
					paletteIdToUpdate: paletteIdToUpdate,
					action: action,
					parentProperty: property
				});
			} else if (property.type === 'NewPalette' && property.palette && this._getRealPaletteId(property.palette.id) === paletteIdToUpdate) {
				const propertyNode = property.$el.find('.property_' + property.id);
				if (action === 'update') {
					propertyNode.remove();
					this._paletteService.getPalette(paletteIdToUpdate).then((palette) => {
						property.palette.content = palette.content;
						property.doRender();
					});
				} else if (action === 'delete') {
					properties.splice(i, 1);
					i--;
					if (properties.length === 0 && parentProperty) {
						parentProperty.$el.find('.collapsibleSectionToggle' + parentProperty.id).remove();
						parentProperty.$el.find('.property_' + parentProperty.id).remove();
					} else {
						propertyNode.remove();
					}
				}
			}
		}
	},

	/**
	 * When a palette gets deleted we need to correctly default to something else if the one being
	 * deleted is the currently selected palette
	 */
	_onPaletteDelete({ paletteId }) {
		if (this._getRealPaletteId(paletteId) === this._getRealPaletteId(this.selectedId)) {
			let newSelectedPalette = null;
			const palette = this._findPaletteFromId(paletteId);

			// We should still have the deleted palette since we have re-renderd.
			if (palette) {
				const section = palette.section;

				// Check the section where the palette was deleted from to see if there are more available
				// palettes in the same section. If so, use that as the selected palette
				for (let i = 0; i < this.palettes[section].length; i++) {
					if (this._getRealPaletteId(this.palettes[section][i].id) !== this._getRealPaletteId(paletteId)) {
						newSelectedPalette = this.palettes[section][i];
						this._sectionOpenState[section] = true;
						break;
					}
				}
			}

			// All else fails defaulkt to the first system palette
			if (!newSelectedPalette && this.palettes.system) {
				newSelectedPalette = this.palettes.system[0];
				this._sectionOpenState['system'] = true;
			}

			if (newSelectedPalette) {
				this.onPaletteChange(this.name, {
					id: newSelectedPalette.id,
					reverse: this.reverse
				});
			}

			this.render();
		} else {
			this._updatePaletteProperties({
				properties: this._propertyUIControl.getProperties(),
				paletteIdToUpdate: paletteId,
				action: 'delete'
			});
		}

	},

	/**
	 * Shifts the selection highlight on to the new palette
	 */
	updateSelection: function () {
		if (this.getPaletteId) {
			const paletteId = this.getPaletteId();
			if (paletteId && paletteId !== this.selectedId) {
				this.selectedId = paletteId;
				// Deselect previous palette, then select the current one
				this.$el && this.$el.find('.paletteColors.selected').removeClass('selected').attr('aria-pressed', 'false');
				this.$el && this.$el.find('.property_' + paletteId).addClass('selected').attr('aria-pressed', 'true');
			}
		}
	},

	remove: function () {
		this._unregisterPaletteServiceEvents();
		if (this.onRemove) {
			this.onRemove();
		}
	},

	render: function () {
		this.$el.parent().addClass('changePalettePane');

		if (this._propertyUIControl) {
			this._propertyUIControl.remove();
			this.$el.empty();
		}

		return this.glassContext.getSvc('.Palette').then((paletteService) => {
			this._paletteService = paletteService;

			return this._getPropertySpec().then((propertySpec) => {
				if (!propertySpec || !propertySpec.items || !propertySpec.items.length) {
					this._registerPaletteServiceEvents();
					return;
				}

				return new Promise((resolve) => {
					require(['baglass/core-client/js/core-client/ui/properties/PropertyUIControl'], (PropertyUIControl) => {
						this._propertyUIControl = new PropertyUIControl(propertySpec);

						return this._propertyUIControl.render().then(() => {
							this._propertyUIControl.focus();
							this._registerPaletteServiceEvents();

							resolve(this._propertyUIControl);
						});
					});
				});
			});
		});
	},

	getPropertyUIControl: function () {
		return this._propertyUIControl;
	}
});

export default ChangePaletteView;



// WEBPACK FOOTER //
// ./src/js/views/ChangePaletteView.js